import React, { useState, useEffect } from "react";
import { Button as BootstrapButton, Col, Row } from "reactstrap";
import {
  Select,
  InputNumber,
  Button,
  Space,
  message,
  Upload,
  Switch,
  Empty,
  Popconfirm,
  Tag,
  Table,
  Typography,
  Alert,
  Form,
} from "antd";
import {
  UploadOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { WorkLineSource, WorkRequestLine } from "../types";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import { nanoid } from "nanoid";
import { useForm, useFormContext, FormProvider } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";

const { Text } = Typography;
const { Option } = Select;

interface NewWorkLineCreatorProps {
  onLineCreated: (line: WorkRequestLine) => void;
  editingLine?: WorkRequestLine | null;
  siteId?: string;
  materialBoqId?: string;
}

const NewWorkLineCreator: React.FC<NewWorkLineCreatorProps> = ({
  onLineCreated,
  editingLine,
  siteId,
  materialBoqId,
}) => {
  const { watch: watchParentForm } = useFormContext() || {};
  const parentFormData = watchParentForm ? watchParentForm() : {};
  const [loading, setLoading] = useState(false);

  // React Hook Form setup
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      workCategory: "",
      workSubCategory: "",
      taskTitle: "",
      taskDescription: "",
      quantity: 1,
      uom: "",
      assignedTower: "",
      assignedVilla: "",
      floorFlatDetails: "",
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = methods;
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [uomList, setUomList] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);

  // Material BOQ states
  const [includeMaterialBOQ, setIncludeMaterialBOQ] = useState<boolean>(false);
  const [materialBoqOptions, setMaterialBoqOptions] = useState<any[]>([]);
  const [materialOptions, setMaterialOptions] = useState<any[]>([]);
  const [selectedMaterialBoqId, setSelectedMaterialBoqId] = useState<any>(
    materialBoqId || ""
  );
  const [allocatedMaterials, setAllocatedMaterials] = useState<any[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>("");
  const [materialQuantity, setMaterialQuantity] = useState<number>(1);
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(
    null
  );
  const [editingQuantity, setEditingQuantity] = useState<number>(0);
  useEffect(() => {
    fetchMasterData();
    if (siteId) {
      fetchMaterialBoqOptions(siteId);
    }
  }, [siteId]);

  useEffect(() => {
    if (selectedMaterialBoqId || materialBoqId) {
      fetchMaterialOptions(selectedMaterialBoqId || materialBoqId);
    }
  }, [selectedMaterialBoqId, materialBoqId]);

  useEffect(() => {
    // Initialize form if editing
    if (editingLine) {
      const category = categories.find(
        (cat) => cat.name === editingLine.category
      );
      const subCategory = subCategories.find(
        (cat) => cat.name === editingLine.subcategory
      );
      const uom = uomList.find((u) => u.code === editingLine.uom);

      setValue("workCategory", category?.id || "");
      setValue("workSubCategory", subCategory?.id || "");
      setValue("taskTitle", editingLine.taskTitle || "");
      setValue("taskDescription", editingLine.taskDescription || "");
      setValue("quantity", editingLine.quantity || 0);
      setValue("uom", uom?.id || "");
      setValue("assignedTower", editingLine.assignedTower || "");
      setValue("assignedVilla", editingLine.assignedVilla || "");

      // Set material allocation data if exists
      if ((editingLine as any).materialAllocation) {
        setAllocatedMaterials((editingLine as any).materialAllocation);
        setIncludeMaterialBOQ(true);
      }

      // Set subcategories for selected category
      if (category) {
        handleCategoryChange(category.id);
      }
    }
  }, [editingLine, categories, subCategories, uomList, setValue]);

  const fetchMasterData = async () => {
    try {
      // Fetch work categories
      const categoriesRes = await dropdownApis.workCategoryDropDown();
      if (categoriesRes.status && categoriesRes.data) {
        const workCategoryOptions = categoriesRes.data.map((item: any) => ({
          id: item?.workCategoryId?.toString(),
          name: item?.workCategoryName,
          description: item?.description || "",
        }));
        setCategories(workCategoryOptions);
      }

      // Fetch UOM options
      const uomRes = await dropdownApis.workUomDropDown();
      if (uomRes.status && uomRes.data) {
        const uomOptions = uomRes.data.map((item: any) => ({
          id: item?.workUomId?.toString(),
          name: item?.uomName,
        }));
        setUomList(uomOptions);
      }
    } catch (error) {
      console.log("Error fetching master data:", error);
      message.error("Failed to fetch master data");
    }
  };

  const fetchMaterialBoqOptions = async (siteId: string) => {
    try {
      const { data, status } = await dropdownApis.materialBOQDropDown(
        parseInt(siteId)
      );
      if (status && data) {
        const materialBoqOptions = data.map((item: any) => ({
          label: item?.targetNumber,
          value: item?.targetId?.toString(),
        }));
        setMaterialBoqOptions(materialBoqOptions);
      }
    } catch (error) {
      console.log("Error fetching material BOQs:", error);
      message.error("Failed to fetch material BOQs");
    }
  };

  const fetchMaterialOptions = async (materialBoqId: string) => {
    try {
      if (!materialBoqId) return;

      const { response: data, status } =
        await dropdownApis.materialBoQMaterialLinesDrioDown(
          parseInt(materialBoqId)
        );
      if (status && data && data.lines) {
        const materialOptions = data.lines.map((item: any) => {
          const boqQty = item?.quantitySet || 0;
          const allocatedQty = item?.requestedQty || 0;
          const pendingQty =
            item?.remainingRequestedQty || boqQty - allocatedQty;

          return {
            label: item?.materialName,
            value: item?.materialId?.toString(),
            uom: item?.uomName,
            uomId: item?.uomId?.toString(),
            category: item?.categoryName,
            categoryId: item?.categoryId?.toString(),
            description: item?.description || item?.materialDescription || "",
            // Quantity fields
            boqQty: boqQty,
            allocatedQty: allocatedQty,
            pendingQty: Math.max(0, pendingQty), // Ensure non-negative
            availableQty: item?.quantityPurchased || 0,
          };
        });
        setMaterialOptions(materialOptions);
      }
    } catch (error) {
      console.log("Error fetching material options:", error);
      message.error("Failed to fetch material options");
    }
  };

  const handleCategoryChange = async (categoryId: string) => {
    try {
      // For now, we'll use empty subcategories as the API endpoint is not available
      // This should be replaced with actual subcategory API call when available
      setSubCategories([]);

      // Clear subcategory when category changes (except during initialization)
      if (!editingLine) {
        setValue("workSubCategory", "");
      }
    } catch (error) {
      console.log("Error fetching subcategories:", error);
    }
  };

  // Material allocation methods
  const getAvailableMaterials = () => {
    const allocatedIds = allocatedMaterials.map(
      (material) => material.materialId
    );
    return materialOptions.filter(
      (option) => !allocatedIds.includes(option.value)
    );
  };

  const handleAddMaterial = () => {
    if (!selectedMaterialId) {
      message.warning("Please select a material first");
      return;
    }

    if (!materialQuantity || materialQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    const materialToAdd = materialOptions.find(
      (material) => material.value === selectedMaterialId
    );

    if (!materialToAdd) {
      message.error("Selected material not found");
      return;
    }

    // Check if already allocated
    if (
      allocatedMaterials.some(
        (material) => material.materialId === selectedMaterialId
      )
    ) {
      message.error("This material has already been allocated");
      return;
    }

    // Validate against pending quantity
    const pendingQty = materialToAdd.pendingQty || 0;
    if (materialQuantity > pendingQty) {
      message.error(
        `Quantity cannot exceed pending quantity (${pendingQty} ${materialToAdd.uom})`
      );
      return;
    }

    if (pendingQty <= 0) {
      message.error("No quantity available for allocation");
      return;
    }

    const newAllocatedMaterial = {
      id: nanoid(),
      materialId: materialToAdd.value,
      materialName: materialToAdd.label,
      quantity: materialQuantity,
      uom: materialToAdd.uom || "nos",
      category: materialToAdd.category,
      // Additional quantity info for reference
      boqQty: materialToAdd.boqQty,
      allocatedQtyBefore: materialToAdd.allocatedQty,
      pendingQtyBefore: materialToAdd.pendingQty,
    };

    const updatedMaterials = [...allocatedMaterials, newAllocatedMaterial];
    setAllocatedMaterials(updatedMaterials);

    setSelectedMaterialId("");
    setMaterialQuantity(1);

    message.success(
      `${materialToAdd.label} (${materialQuantity} ${materialToAdd.uom}) added successfully`
    );
  };

  const handleRemoveMaterial = (materialId: string) => {
    const materialToRemove = allocatedMaterials.find(
      (m) => m.id === materialId
    );
    const updatedMaterials = allocatedMaterials.filter(
      (material) => material.id !== materialId
    );

    setAllocatedMaterials(updatedMaterials);
    message.success(`${materialToRemove?.materialName} removed successfully`);
  };

  const handleStartEdit = (materialId: string, currentQuantity: number) => {
    setEditingMaterialId(materialId);
    setEditingQuantity(currentQuantity);
  };

  const handleStartEditInline = (
    materialValue: string,
    currentQuantity: number
  ) => {
    setEditingMaterialId(materialValue);
    setEditingQuantity(currentQuantity);
  };

  const handleSaveEdit = (materialId: string) => {
    if (editingQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    const updatedMaterials = allocatedMaterials.map((material) =>
      material.id === materialId
        ? { ...material, quantity: editingQuantity }
        : material
    );

    setAllocatedMaterials(updatedMaterials);
    setEditingMaterialId(null);
    message.success("Quantity updated successfully");
  };

  const handleSaveEditInline = (materialValue: string) => {
    if (editingQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    const materialToEdit = materialOptions.find(
      (m) => m.value === materialValue
    );
    const pendingQty = materialToEdit?.pendingQty || 0;
    const currentAllocated = allocatedMaterials.find(
      (m) => m.materialId === materialValue
    );

    // Allow editing up to pending + current allocated quantity
    const maxAllowed = pendingQty + (currentAllocated?.quantity || 0);

    if (editingQuantity > maxAllowed) {
      message.error(
        `Quantity cannot exceed ${maxAllowed} ${
          materialToEdit?.uom || ""
        } (available + currently allocated)`
      );
      return;
    }

    const updatedMaterials = allocatedMaterials.map((material) =>
      material.materialId === materialValue
        ? { ...material, quantity: editingQuantity }
        : material
    );

    setAllocatedMaterials(updatedMaterials);
    setEditingMaterialId(null);
    message.success("Quantity updated successfully");
  };

  const handleCancelEdit = () => {
    setEditingMaterialId(null);
    setEditingQuantity(0);
  };

  const submitForm = async (values: any) => {
    try {
      setLoading(true);

      // Find category and subcategory names
      const selectedCategory = categories.find(
        (cat) => cat.id === values.workCategory
      );
      const selectedSubCategory = subCategories.find(
        (cat) => cat.id === values.workSubCategory
      );
      const selectedUOM = uomList.find((uom) => uom.id === values.uom);

      // Create work request line
      const workRequestLine: any = {
        id: editingLine?.id || `${Date.now()}-${Math.random()}`,
        taskTitle: values.taskTitle.trim(),
        taskDescription: values.taskDescription?.trim(),
        category: selectedCategory?.name || values.workCategory,
        subcategory: selectedSubCategory?.name,
        quantity: values.quantity,
        uom: selectedUOM?.code || values.uom,
        source: WorkLineSource.NewWorkLine,
        assignedTower: values.assignedTower,
        assignedVilla: values.assignedVilla,
        documents: fileList.map((file) => file.name) || [],
        materialAllocation: includeMaterialBOQ ? allocatedMaterials : [],
        // Include IDs for API payload  
        workCategoryId: values.workCategory,
        workSubCategoryId: values.workSubCategory,
        uomId: values.uom,
      };

      onLineCreated(workRequestLine);

      if (!editingLine) {
        reset();
        setFileList([]);
        setSubCategories([]);
        setAllocatedMaterials([]);
        setIncludeMaterialBOQ(false);
        setSelectedMaterialId("");
        setMaterialQuantity(1);
        message.success("Work line created successfully");
      } else {
        message.success("Work line updated successfully");
      }
    } catch (error) {
      console.log("Error creating work line:", error);
      message.error("Failed to create work line");
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    fileList,
    onChange: ({ fileList: newFileList }: any) => setFileList(newFileList),
    beforeUpload: () => false, // Prevent auto upload
    multiple: true,
  };

  // Tower options
  const towerOptions = ["Tower A", "Tower B", "Tower C", "Tower D"];

  // Villa options
  const villaOptions = ["Villa 1", "Villa 2", "Villa 3", "Villa 4", "Villa 5"];

  return (
    <div>
      {editingLine && (
        <div className="alert alert-info mb-3">
          <strong>Edit Mode:</strong> Editing {editingLine.taskTitle} - Only
          quantity and location can be modified
        </div>
      )}

      <FormProvider {...methods}>
        <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
          <div className="card">
            <div className="card-body">
              <Row className="gy-4">
                {/* Work Category - Mandatory (FSD Field 1) */}
                <Col lg="6">
                  <FormSelect
                    name="workCategory"
                    label="Work Category"
                    options={categories.map((cat) => ({
                      label:
                        cat.name +
                        (cat.description ? ` - ${cat.description}` : ""),
                      value: cat.id,
                    }))}
                    required={true}
                    placeholder="Work type category"
                    disabled={!!editingLine}
                    onChange={handleCategoryChange}
                  />
                </Col>

                {/* Work Sub Category - Mandatory (FSD Field 2) */}
                {/* <Col lg="6">
                  <FormSelect
                    name="workSubCategory"
                    label="Work Sub Category"
                    options={subCategories.map((cat) => ({
                      label: cat.name,
                      value: cat.id
                    }))}
                    required={true}
                    placeholder="Work subcategory"
                    disabled={!!editingLine || subCategories.length === 0}
                  />
                </Col> */}

                {/* Task Title - Mandatory (FSD Field 3) */}
                <Col lg="6">
                  <FormInput
                    name="taskTitle"
                    label="Task Title"
                    required={true}
                    placeholder="Task heading"
                    maxLength={200}
                    disabled={!!editingLine}
                  />
                </Col>

                {/* Task Description - Optional (FSD Field 4) */}
                <Col lg="12">
                  <FormTextArea
                    name="taskDescription"
                    label="Task Description"
                    placeholder="Description of task"
                    rows={3}
                    maxLength={500}
                    disabled={!!editingLine}
                  />
                </Col>

                {/* Quantity - Mandatory (FSD Field 5) */}
                <Col lg="6">
                  <FormInput
                    name="quantity"
                    label="Quantity"
                    type="number"
                    required={true}
                    placeholder="Required work quantity"
                    min={0.01}
                    // step={0.01}
                  />
                </Col>

                {/* UOM - Mandatory (FSD Field 6) */}
                <Col lg="6">
                  <FormSelect
                    name="uom"
                    label="UOM"
                    options={uomList.map((uom) => ({
                      label: `${uom.name}`,
                      value: uom.id,
                    }))}
                    required={true}
                    placeholder="Unit of Measurement"
                    disabled={!!editingLine}
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="card-title">
                <h6>Location & Assignment</h6>
              </div>
              <Row className="gy-4">
                {/* Tower / Villa Select - Conditional (FSD Field 8) */}
                <Col lg="6">
                  <FormSelect
                    name="assignedTower"
                    label="Tower Select"
                    options={towerOptions.map((tower) => ({
                      label: tower,
                      value: tower,
                    }))}
                    placeholder="Select Tower"
                    // allowClear
                  />
                </Col>

                <Col lg="6">
                  <FormSelect
                    name="assignedVilla"
                    label="Villa Select"
                    options={villaOptions.map((villa) => ({
                      label: villa,
                      value: villa,
                    }))}
                    placeholder="Select Villa"
                    // allowClear
                  />
                </Col>

                {/* Floor / Flat Details - Conditional (FSD Field 9) */}
                <Col lg="12">
                  <FormInput
                    name="floorFlatDetails"
                    label="Floor / Flat Details"
                    placeholder="Further filters based on tower/villa selection"
                    disabled={!!editingLine}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* Material BOQ Section */}
          <div className="card">
            <div className="card-body">
              <div className="card-title">
                <h6>Material Allocation</h6>
              </div>
              <div className="mb-3">
                <label className="form-label d-flex align-items-center mb-2">
                  Include Material BOQ
                  <Switch
                    checked={includeMaterialBOQ}
                    onChange={(checked) => {
                      setIncludeMaterialBOQ(checked);
                      if (!checked) {
                        setAllocatedMaterials([]);
                        setSelectedMaterialId("");
                        setMaterialQuantity(1);
                        setSelectedMaterialBoqId("");
                      }
                    }}
                    className="ms-2"
                    disabled={!!editingLine}
                  />
                </label>
                <div className="form-note text-muted">
                  Enable to allocate materials from Material BOQ to this work
                  line
                </div>
              </div>

              {/* Material BOQ Selection */}
              {includeMaterialBOQ && !materialBoqId && (
                <div className="mb-3">
                  <label className="form-label mb-2">
                    Select Material BOQ <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Material BOQ"
                    value={selectedMaterialBoqId || undefined}
                    onChange={(value) => {
                      setSelectedMaterialBoqId(value);
                      setAllocatedMaterials([]);
                      setSelectedMaterialId("");
                    }}
                    style={{ width: "100%" }}
                    showSearch
                    filterOption={(input, option) =>
                      option?.children
                        ?.toString()
                        .toLowerCase()
                        .includes(input.toLowerCase()) || false
                    }
                  >
                    {materialBoqOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                  {!selectedMaterialBoqId &&
                    materialBoqOptions.length === 0 && (
                      <div className="text-muted mt-1">
                        No Material BOQs available for selected site
                      </div>
                    )}
                </div>
              )}

              {includeMaterialBOQ && (
                <div>
                  {/* Unified Material Selection & Allocation Table */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="card-title d-flex justify-content-between align-items-center">
                        <div>
                          <h6>Material Selection & Allocation</h6>
                        </div>
                        {allocatedMaterials.length > 0 && (
                          <div className="text-end">
                            <div className="fw-bold text-success">
                              {allocatedMaterials.length} Materials Allocated
                            </div>
                            <div className="text-muted small">
                              Total:{" "}
                              {allocatedMaterials.reduce(
                                (sum, m) => sum + m.quantity,
                                0
                              )}{" "}
                              items
                            </div>
                          </div>
                        )}
                      </div>

                      {(selectedMaterialBoqId || materialBoqId) &&
                      materialOptions.length > 0 ? (
                        <Table
                          columns={[
                            {
                              title: "Material",
                              key: "material",
                              width: "28%",
                              render: (_: any, record: any) => {
                                const allocatedMaterial =
                                  allocatedMaterials.find(
                                    (material) =>
                                      material.materialId === record.value
                                  );
                                const isEditing =
                                  editingMaterialId === record.value;
                                const pendingQty = record.pendingQty || 0;

                                return (
                                  <div>
                                    <div className="fw-bold">
                                      {record.label}
                                    </div>
                                  </div>
                                );
                              },
                            },
                            {
                              title: "category",
                              dataIndex: "category",
                              key: "category",
                              width: "11%",
                            },
                            {
                              title: "BOQ Qty",
                              dataIndex: "boqQty",
                              key: "boqQty",
                              width: "11%",
                              align: "right" as const,
                              render: (qty: number, record: any) => (
                                <div className="text-center">
                                  <div className="fw-bold">
                                    {qty?.toLocaleString()}
                                  </div>
                                  <div className="text-muted small">
                                    {record.uom}
                                  </div>
                                </div>
                              ),
                            },
                            {
                              title: "Allocated Qty",
                              dataIndex: "allocatedQty",
                              key: "allocatedQty",
                              width: "11%",
                              align: "right" as const,
                              render: (qty: number, record: any) => (
                                <div className="text-center">
                                  <div className="text-warning fw-bold">
                                    {qty?.toLocaleString()}
                                  </div>
                                  <div className="text-muted small">
                                    {record.uom}
                                  </div>
                                </div>
                              ),
                            },
                            {
                              title: "Pending Qty",
                              dataIndex: "pendingQty",
                              key: "pendingQty",
                              width: "11%",
                              align: "right" as const,
                              render: (qty: number, record: any) => {
                                const isLow = qty <= 0;
                                return (
                                  <div className="text-center">
                                    <div
                                      className={`fw-bold ${
                                        isLow ? "text-danger" : "text-success"
                                      }`}
                                    >
                                      {qty?.toLocaleString()}
                                    </div>
                                    <div className="text-muted small">
                                      {record.uom}
                                    </div>
                                  </div>
                                );
                              },
                            },
                            {
                              title: "Qty To Allocate",
                              key: "myAllocation",
                              width: "18%",
                              render: (_: any, record: any) => {
                                const allocatedMaterial =
                                  allocatedMaterials.find(
                                    (material) =>
                                      material.materialId === record.value
                                  );
                                const isSelected =
                                  selectedMaterialId === record.value;
                                const pendingQty = record.pendingQty || 0;
                                const isEditing =
                                  editingMaterialId === record.value;

                                // If material is already allocated and being edited
                                if (allocatedMaterial && isEditing) {
                                  return (
                                    <div className="d-flex align-items-center gap-1">
                                      <InputNumber
                                        min={0.01}
                                        max={
                                          pendingQty +
                                          allocatedMaterial.quantity
                                        }
                                        step={0.01}
                                        value={editingQuantity}
                                        onChange={(value) =>
                                          setEditingQuantity(value || 0)
                                        }
                                        style={{ width: "70px" }}
                                        size="small"
                                      />
                                      <Button
                                        type="text"
                                        icon={<CheckOutlined />}
                                        onClick={() =>
                                          handleSaveEditInline(record.value)
                                        }
                                        size="small"
                                        style={{ color: "#52c41a" }}
                                      />
                                      <Button
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={handleCancelEdit}
                                        size="small"
                                        style={{ color: "#ff4d4f" }}
                                      />
                                    </div>
                                  );
                                }

                                // If material is already allocated (display mode)
                                if (allocatedMaterial) {
                                  return (
                                    <div className="text-center">
                                      <div
                                        className="fw-bold text-success"
                                        style={{ fontSize: "16px" }}
                                      >
                                        {allocatedMaterial.quantity?.toLocaleString()}
                                      </div>
                                      <div className="text-muted small">
                                        {record.uom} allocated
                                      </div>
                                    </div>
                                  );
                                }

                                // If no quantity available
                                if (pendingQty <= 0) {
                                  return (
                                    <div className="text-center">
                                      <div className="text-danger">-</div>
                                      <div className="text-muted small">
                                        No stock
                                      </div>
                                    </div>
                                  );
                                }

                                // Input for new allocation
                                return (
                                  <InputNumber
                                    min={0.01}
                                    max={pendingQty}
                                    step={0.01}
                                    value={
                                      isSelected ? materialQuantity : undefined
                                    }
                                    onChange={(value) => {
                                      setSelectedMaterialId(record.value);
                                      setMaterialQuantity(value || 0.01);
                                    }}
                                    placeholder="Enter qty"
                                    style={{ width: "100%" }}
                                    size="small"
                                  />
                                );
                              },
                            },
                            {
                              title: "Actions",
                              key: "actions",
                              width: "21%",
                              render: (_: any, record: any) => {
                                const allocatedMaterial =
                                  allocatedMaterials.find(
                                    (material) =>
                                      material.materialId === record.value
                                  );
                                const isSelected =
                                  selectedMaterialId === record.value;
                                const pendingQty = record.pendingQty || 0;
                                const hasQuantity =
                                  materialQuantity > 0 &&
                                  materialQuantity <= pendingQty;
                                const isEditing =
                                  editingMaterialId === record.value;

                                // If material is allocated, show edit and remove options
                                if (allocatedMaterial && !isEditing) {
                                  return (
                                    <Space>
                                      <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                          handleStartEditInline(
                                            record.value,
                                            allocatedMaterial.quantity
                                          )
                                        }
                                        size="small"
                                        style={{ padding: "2px 4px" }}
                                      >
                                        Edit
                                      </Button>
                                      <Popconfirm
                                        title="Remove Material"
                                        description={`Remove ${allocatedMaterial.materialName} allocation?`}
                                        onConfirm={() =>
                                          handleRemoveMaterial(
                                            allocatedMaterial.id
                                          )
                                        }
                                        okText="Yes"
                                        cancelText="No"
                                      >
                                        <Button
                                          type="link"
                                          danger
                                          icon={<DeleteOutlined />}
                                          size="small"
                                          style={{ padding: "2px 4px" }}
                                        >
                                          Remove
                                        </Button>
                                      </Popconfirm>
                                    </Space>
                                  );
                                }

                                // If editing inline, show helper text
                                if (isEditing) {
                                  return (
                                    <div className="text-muted small text-center">
                                      Use ✓ or ✕ buttons
                                      <br />
                                      to save or cancel
                                    </div>
                                  );
                                }

                                // If no pending quantity, show disabled state
                                if (pendingQty <= 0) {
                                  return (
                                    <div className="text-center">
                                      <Button size="small" disabled>
                                        No Stock
                                      </Button>
                                    </div>
                                  );
                                }

                                // Show add button for new allocation
                                return (
                                  <div className="text-center">
                                    <Button
                                      type="primary"
                                      size="small"
                                      icon={<PlusOutlined />}
                                      onClick={() => {
                                        setSelectedMaterialId(record.value);
                                        handleAddMaterial();
                                      }}
                                      disabled={!isSelected || !hasQuantity}
                                    >
                                      {isSelected && hasQuantity
                                        ? "Allocate"
                                        : "Enter Qty"}
                                    </Button>
                                  </div>
                                );
                              },
                            },
                          ]}
                          dataSource={materialOptions}
                          rowKey="value"
                          // pagination={{
                          //   pageSize: 8,
                          //   size: "small",
                          //   showSizeChanger: true,
                          //   showQuickJumper: true,
                          //   showTotal: (total, range) => {
                          //     const allocatedCount = allocatedMaterials.length;
                          //     return `${range[0]}-${range[1]} of ${total} materials (${allocatedCount} allocated)`;
                          //   },
                          // }}
                          className="custom-table"
                          size="small"
                          scroll={{ x: 800 }}
                          bordered
                        />
                      ) : (
                        <div>
                          {!selectedMaterialBoqId && !materialBoqId ? (
                            <Alert
                              message="Select Material BOQ First"
                              description="Please select a Material BOQ to view available materials."
                              type="warning"
                              showIcon
                            />
                          ) : materialOptions.length === 0 ? (
                            <Alert
                              message="Loading Materials..."
                              description="Fetching materials from selected Material BOQ."
                              type="info"
                              showIcon
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!editingLine && (
            <div className="card">
              <div className="card-body">
                <div className="card-title">
                  <h6>Additional Information</h6>
                </div>
                <Row className="gy-4">
                  {/* Allocate Material - Section Grid (FSD Field 7) */}
                  {/* <Col span={24}>
                <Form.Item name="allocateMaterial" label="Allocate Material">
                  <div className="p-3 border rounded bg-light">
                    <p className="text-muted mb-0">
                      <strong>Material Allocation:</strong> Allocated materials
                      from Material BOQ if applicable. This section will be
                      implemented with material linking functionality.
                    </p>
                  </div>
                </Form.Item>
              </Col> */}

                  {/* Upload Documents - Optional (FSD Field 10) */}
                  <Col lg="12">
                    <Form.Item name="documents" label="Upload Documents">
                      <Upload {...uploadProps}>
                        <Button icon={<UploadOutlined />}>
                          Attach supporting documents
                        </Button>
                      </Upload>
                      {fileList.length > 0 && (
                        <div className="mt-1">
                          <small className="text-muted">
                            {fileList.length} file(s) selected
                          </small>
                        </div>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="actions clearfix">
            <ul>
              <li>
                <BootstrapButton
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  <CheckOutlined className="me-1" />
                  {editingLine ? "Update Work Line" : "Create Work Line"}
                </BootstrapButton>
              </li>
            </ul>
          </div>
        </form>
      </FormProvider>

      {/* Custom styles for table rows */}

      {/* FSD Note */}
      {/* <div className="mt-3 p-2 bg-light rounded">
        <small className="text-muted">
          <strong>FSD Reference:</strong> New Work Line Creation (Similar to Work BOQ Lines).
          📌 Users can add/edit/remove work lines dynamically.
        </small>
      </div> */}
    </div>
  );
};

export default NewWorkLineCreator;
